import { Component, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { forkJoin } from "rxjs";
import {
  CapacitacionProgramacionService,
} from "../../../../../../shared/services/capacitaciones_programacion.service";
import { CapacitacionInvitadosService } from "../../../../../../shared/services/capacitaciones_invitados.service";

declare var require: any;
const Swal = require("sweetalert2");

/**
 * Estado de un tema puntual (hereda el horario de su actividad padre):
 *  - 'antes'   → todavía no ha comenzado
 *  - 'activo'  → estamos dentro del rango hora_inicio–hora_fin
 *  - 'despues' → ya finalizó
 */
type EstadoHorario = "antes" | "activo" | "despues";

// ── Estructura tal como llega del backend ─────────────────────────────
interface CapacitacionActividadReRaw {
  id: number;
  id_actividad: number;
  actividad: string; // nombre del tema
  estado: number;
}

interface ActividadRaw {
  id: number;
  id_capacitacion: number;
  dirigido?: number | string;
  responsable?: number | string;
  fecha_programacion: string; // YYYY-MM-DD
  hora_inicio: string; // HH:mm[:ss]
  hora_fin: string; // HH:mm[:ss]
  estado?: number;
  CapacitacionActividadReData?: CapacitacionActividadReRaw[];
}

// ── Estructura aplanada usada en pantalla: un item por CADA TEMA ──────
interface TemaAsistenciaUI {
  id_tema: number; // id de CapacitacionActividadRe -> lo que realmente se confirma
  nombre_tema: string; // actividad.actividad (el texto del tema)
  id_actividad: number; // id de CapacitacionActividad (dueño del horario)
  fecha_programacion: string;
  hora_inicio: string;
  hora_fin: string;
  estado_horario: EstadoHorario;
  inicio: Date | null;
  fin: Date | null;
  fechaFormateada: string;
  horaInicioFormateada: string;
  horaFinFormateada: string;
}

@Component({
  selector: "app-invitacion",
  templateUrl: "./invitacion.component.html",
  styleUrls: ["./invitacion.component.scss"],
})
export class InvitacionComponent implements OnInit {
  // ── Parámetros de la URL ──────────────────────────────────────────
  programacionId = 0;
  tenantId = 0;
  capacitacionNombre = "";

  // ── Datos del formulario de confirmación ─────────────────────────
  cedula = "";
  nombre = "";

  // ── Estado de carga / UI ──────────────────────────────────────────
  cargando = true;
  errorCarga = "";
  isEnviando = false;
  confirmado = false;

  // ── Temas (aplanados desde actividades -> CapacitacionActividadReData) ──
  temas: TemaAsistenciaUI[] = [];

  // 🔹 Ahora se pueden seleccionar VARIOS temas a la vez
  temasSeleccionados: TemaAsistenciaUI[] = [];

  constructor(
    private route: ActivatedRoute,
    private capacitacionProgramacionService: CapacitacionProgramacionService,
    private invitadosService: CapacitacionInvitadosService,
  ) {}

  ngOnInit(): void {
    this.programacionId = Number(this.route.snapshot.paramMap.get("id") ?? 0);
    this.tenantId = Number(this.route.snapshot.paramMap.get("tenantId") ?? 0);

    if (!this.programacionId) {
      this.cargando = false;
      this.errorCarga = "El enlace de invitación no es válido.";
      return;
    }

    this.cargarProgramacion();
  }

  // ── Carga la programación + actividades + temas, y calcula estados ──
  private cargarProgramacion(): void {
    this.cargando = true;
    this.temasSeleccionados = [];

    this.capacitacionProgramacionService
      .getCapacitacionProgramacionByIdPublica(this.programacionId, this.tenantId)
      .subscribe({
        next: (data: any) => {
          this.cargando = false;
          this.capacitacionNombre = data?.capacitacion?.nombre ?? "";

          const actividadesRaw: ActividadRaw[] = data?.actividades ?? [];
          this.temas = this.aplanarTemas(actividadesRaw);

          // Preselecciona automáticamente los temas activos en este momento
          // (el usuario puede deseleccionar o agregar más si hay varios)
          this.temasSeleccionados = [...this.temasActivos];
        },
        error: () => {
          this.cargando = false;
          this.errorCarga = "No se pudo cargar la información de la invitación.";
        },
      });
  }

  private aplanarTemas(actividades: ActividadRaw[]): TemaAsistenciaUI[] {
    const resultado: TemaAsistenciaUI[] = [];

    actividades.forEach((actividad) => {
      const temasDeLaActividad = actividad.CapacitacionActividadReData ?? [];

      temasDeLaActividad
        .filter((t) => Number(t.estado) === 1)
        .forEach((tema) => {
          resultado.push(this.evaluarTema(actividad, tema));
        });
    });

    return resultado.sort((a, b) => {
      const fa = `${a.fecha_programacion ?? ""} ${a.hora_inicio ?? ""}`;
      const fb = `${b.fecha_programacion ?? ""} ${b.hora_inicio ?? ""}`;
      return fa.localeCompare(fb);
    });
  }

  private evaluarTema(
    actividad: ActividadRaw,
    tema: CapacitacionActividadReRaw,
  ): TemaAsistenciaUI {
    const inicio = this.combinarFechaHora(actividad.fecha_programacion, actividad.hora_inicio);
    const fin = this.combinarFechaHora(actividad.fecha_programacion, actividad.hora_fin);
    const ahora = new Date();

    let estado_horario: EstadoHorario;
    if (!inicio || !fin) {
      estado_horario = "despues";
    } else if (ahora < inicio) {
      estado_horario = "antes";
    } else if (ahora > fin) {
      estado_horario = "despues";
    } else {
      estado_horario = "activo";
    }

    return {
      id_tema: tema.id,
      nombre_tema: tema.actividad,
      id_actividad: actividad.id,
      fecha_programacion: actividad.fecha_programacion,
      hora_inicio: actividad.hora_inicio,
      hora_fin: actividad.hora_fin,
      estado_horario,
      inicio,
      fin,
      fechaFormateada: this.formatearFecha(actividad.fecha_programacion),
      horaInicioFormateada: this.formatearHora(actividad.hora_inicio),
      horaFinFormateada: this.formatearHora(actividad.hora_fin),
    };
  }

  // ── Selección múltiple ───────────────────────────────────────────────
  estaSeleccionado(tema: TemaAsistenciaUI): boolean {
    return this.temasSeleccionados.some((t) => t.id_tema === tema.id_tema);
  }

  toggleTema(tema: TemaAsistenciaUI): void {
    if (tema.estado_horario !== "activo") return; // no se puede marcar un tema fuera de horario

    if (this.estaSeleccionado(tema)) {
      this.temasSeleccionados = this.temasSeleccionados.filter((t) => t.id_tema !== tema.id_tema);
    } else {
      this.temasSeleccionados = [...this.temasSeleccionados, tema];
    }
  }

  // ── Getters usados por el HTML ──────────────────────────────────────
  get temasActivos(): TemaAsistenciaUI[] {
    return this.temas.filter((t) => t.estado_horario === "activo");
  }

  get proximoTema(): TemaAsistenciaUI | null {
    const pendientes = this.temas
      .filter((t) => t.estado_horario === "antes" && t.inicio)
      .sort((x, y) => (x.inicio as Date).getTime() - (y.inicio as Date).getTime());
    return pendientes[0] ?? null;
  }

  get sinTemas(): boolean {
    return this.temas.length === 0;
  }

  get todosFinalizados(): boolean {
    return this.temas.length > 0 && this.temas.every((t) => t.estado_horario === "despues");
  }

  // ── Helpers de fecha/hora ────────────────────────────────────────────
  private combinarFechaHora(fecha: string, hora: string): Date | null {
    if (!fecha || !hora) return null;
    const parteFecha = String(fecha).trim().split(/[ T]/)[0];
    const fechaCompleta = new Date(`${parteFecha}T${hora}`);
    return isNaN(fechaCompleta.getTime()) ? null : fechaCompleta;
  }

  formatearHora(hora: string): string {
    if (!hora) return "";
    try {
      const fecha = new Date(`1970-01-01T${hora}`);
      return fecha.toLocaleTimeString("es-CO", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return hora;
    }
  }

  formatearFecha(fecha: string): string {
    if (!fecha) return "";
    try {
      const parteFecha = String(fecha).trim().split(/[ T]/)[0];
      const f = new Date(`${parteFecha}T12:00:00`);
      return f.toLocaleDateString("es-CO", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    } catch {
      return fecha;
    }
  }

  // ── Formulario ────────────────────────────────────────────────────
  get formularioValido(): boolean {
    return (
      String(this.cedula).trim().length > 0 &&
      this.nombre.trim().length > 0 &&
      this.temasSeleccionados.length > 0
    );
  }

  confirmarAsistencia(): void {
    if (this.temasSeleccionados.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Selecciona al menos un tema",
        text: "Marca los temas a los que estás asistiendo en este momento.",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    if (!this.formularioValido) {
      Swal.fire({
        icon: "warning",
        title: "Datos incompletos",
        text: "Por favor ingresa tu número de documento y tu nombre completo.",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    const listaTemas = this.temasSeleccionados
      .map((t) => `• ${t.nombre_tema} (${t.horaInicioFormateada} – ${t.horaFinFormateada})`)
      .join("<br>");

    Swal.fire({
      icon: "question",
      title: "¿Confirmar asistencia?",
      html: `
        <p class="mb-1">¿Deseas confirmar tu asistencia a ${this.temasSeleccionados.length > 1 ? "estos temas" : "este tema"}?</p>
        <br>
        <strong>Capacitación:</strong> ${this.capacitacionNombre || "-"}<br><br>
        ${listaTemas}
        <br><br>
        <strong>Nombre:</strong> ${this.nombre.trim()}<br>
        <strong>N° Documento:</strong> ${this.cedula.toString()}
      `,
      showCancelButton: true,
      confirmButtonText: "Sí, confirmar",
      cancelButtonText: "Revisar",
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#6c757d",
    }).then((result: any) => {
      if (!result.isConfirmed) return;

      this.isEnviando = true;

      const peticiones = this.temasSeleccionados.map((tema) =>
        this.invitadosService.confirmarAsistenciaPublica(
          {
            capacitacion_programacion_id: this.programacionId,
            actividad_id: tema.id_tema, // 🔹 id del TEMA (CapacitacionActividadRe)
            propietario_id: Number(String(this.cedula).trim()),
            nombre: this.nombre.trim().toUpperCase(),
            observaciones: "Confirmado desde invitación",
          },
          this.tenantId,
        ),
      );

      forkJoin(peticiones).subscribe({
        next: () => {
          this.isEnviando = false;
          this.confirmado = true;
          Swal.fire({
            icon: "success",
            title: "¡Asistencia confirmada!",
            text: "Tu asistencia ha sido registrada correctamente. ¡Te esperamos!",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#3085d6",
          });
        },
        error: (err) => {
          this.isEnviando = false;
          Swal.fire({
            icon: "error",
            title: "No se pudo confirmar toda tu asistencia",
            text:
              err?.error?.message ||
              "Alguno de los temas no pudo registrarse. Revisa e intenta de nuevo.",
            confirmButtonColor: "#f64e60",
          });
          // Refrescamos por si algún tema venció justo al momento de enviar
          this.cargarProgramacion();
        },
      });
    });
  }
}